import React, { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import { collection, getDocs, addDoc, setDoc, getDoc, doc, query, where } from "firebase/firestore";
import LatexRenderer from "./LatexRenderer";

const CLASSES = ["PUC-I", "PUC-II", "Engineering Physics"];
const ADMIN_EMAIL = "g.devaraju@gmail.com";

// Deterministic Firestore document ID for a student's quiz attempt.
// Using a fixed ID means: (1) setDoc naturally prevents duplicates,
// (2) getDoc is a direct read — no query, no index, no security-rule surprise.
const attemptDocId = (quizId, email) =>
  `${quizId}_${email.replace(/[^a-zA-Z0-9]/g, "_")}`;

const fmtWhen = iso => new Date(iso).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
const fmtTimeOnly = iso => new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
const scheduleText = quiz => {
  const { openFrom: f, openUntil: u } = quiz;
  if (f && u) return sameDay(f, u) ? `${fmtWhen(f)} – ${fmtTimeOnly(u)}` : `${fmtWhen(f)} → ${fmtWhen(u)}`;
  if (f) return `From ${fmtWhen(f)}`;
  if (u) return `Until ${fmtWhen(u)}`;
  return "Open anytime";
};
const countdown = ms => { const m = Math.max(0, Math.round(ms / 60000)); const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60; return d ? `${d}d ${h}h` : h ? `${h}h ${mm}m` : `${mm}m`; };

const MAX_VIOLATIONS = 3; // leaving the quiz screen this many times auto-submits

const scoreAnswers = (questions, ans = {}) => {
  let score = 0;
  questions.forEach((q, i) => {
    if (ans[i] !== undefined) {
      if (q.type === "truefalse") { if (String(ans[i]) === String(q.answer)) score++; }
      else { if (Number(ans[i]) === Number(q.answer)) score++; }
    }
  });
  return score;
};

// An attempt left "in_progress" (tab closed / refreshed / phone locked) is finalised
// with whatever answers were saved — the student cannot restart it.
const finaliseAbandoned = async (quiz, data) => {
  const questions = quiz.questions || [];
  const score = scoreAnswers(questions, data.studentAnswers || {});
  const total = questions.length || data.total || 0;
  const done = { ...data, status: "submitted", score, total, percent: total ? Math.round((score / total) * 100) : 0,
    autoSubmitted: data.autoSubmitted || "Left the quiz before submitting", submittedAt: new Date().toISOString(), timestamp: Date.now() };
  await setDoc(doc(db, "quizResults", attemptDocId(quiz.id, data.studentEmail)), done);
  return done;
};

const CODE_OF = { "PUC-I": "P1", "PUC-II": "P2", "Engineering Physics": "E1" };

export default function QuizPage({ user, userAccess = [], role = "student" }) {
  const [activeClass, setActiveClass] = useState("Engineering Physics");
  const [classMap, setClassMap] = useState({});        // facultyClassId → { name, member }
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [reviewQuiz, setReviewQuiz] = useState(null);   // quiz to review answers for
  const [studentBatch, setStudentBatch] = useState(null);
  const [batchLoading, setBatchLoading] = useState(true);
  const [attemptedQuizIds, setAttemptedQuizIds] = useState(new Set());
  // Stores full result data per quizId so review screen doesn't need another Firestore query
  const [attemptedResults, setAttemptedResults] = useState({});

  const isAdmin = user?.email === ADMIN_EMAIL;
  const isStaff = isAdmin || role === "teacher";
  // Students only see class tabs they have access to
  const visibleClasses = isStaff ? CLASSES : CLASSES.filter(c => userAccess.includes(CODE_OF[c]));
  useEffect(() => {
    if (!isStaff && visibleClasses.length && !visibleClasses.includes(activeClass)) setActiveClass(visibleClasses[0]);
  }, [visibleClasses.join(","), isStaff]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch student batch
  useEffect(() => {
    const fetchBatch = async () => {
      if (isAdmin) { setStudentBatch("ADMIN"); setBatchLoading(false); return; }
      setBatchLoading(true);
      try {
        const q = query(collection(db, "studentRoster"), where("email", "==", user.email));
        const snap = await getDocs(q);
        if (!snap.empty) setStudentBatch(snap.docs[0].data().batch || null);
        else setStudentBatch(null);
      } catch { setStudentBatch(null); }
      setBatchLoading(false);
    };
    fetchBatch();
  }, [user.email]);

  // Fetch all quiz results for this student.
  // Primary: query by studentEmail (works for both old auto-ID docs and new deterministic-ID docs).
  // Also stores full result data so review screen can show score + answers without re-querying.
  useEffect(() => {
    if (isAdmin) return;
    const fetchAttempts = async () => {
      try {
        const q = query(collection(db, "quizResults"), where("studentEmail", "==", user.email));
        const snap = await getDocs(q);
        const ids = new Set();
        const results = {};
        snap.docs.forEach(d => {
          const data = d.data();
          ids.add(data.quizId);
          // Keep the most recent result if somehow multiple exist
          if (!results[data.quizId] || data.timestamp > results[data.quizId].timestamp) {
            results[data.quizId] = data;
          }
        });
        setAttemptedQuizIds(ids);
        setAttemptedResults(results);
      } catch (e) {
        console.error("fetchAttempts error (query):", e);
        // If the query fails (e.g. Firestore rules), we fall back to per-quiz getDoc checks
        // inside QuizRunner itself — so students still can't retake even if this query fails.
      }
    };
    fetchAttempts();
  }, [user.email, isAdmin]);

  useEffect(() => {
    if (!user?.email || batchLoading) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "facultyClasses"));
        const me = user.email.toLowerCase();
        const m = {};
        snap.docs.forEach(d => {
          const c = d.data();
          const member = (c.studentEmails || []).map(e => (e || "").toLowerCase()).includes(me)
            || (c.batch && (c.batch === "Both" || c.batch === studentBatch));
          m[d.id] = { name: c.name || "Class", member };
        });
        setClassMap(m);
      } catch (e) { console.warn("facultyClasses load failed", e); }
    })();
  }, [user?.email, batchLoading, studentBatch]);

  useEffect(() => { if (user?.email) fetchQuizzes(); }, [activeClass, user?.email]);

  // Which quizzes a student may see on this page
  const canSee = (quiz) => {
    if (isStaff) return true;
    if (quiz.facultyClassId) return !!classMap[quiz.facultyClassId]?.member;   // class-only quiz
    return userAccess.includes(CODE_OF[quiz.class]);                            // general quiz
  };
  const shownQuizzes = quizzes.filter(canSee);

  const fetchQuizzes = async () => {
    setLoading(true);
    const q = query(collection(db, "quizzes"), where("class", "==", activeClass));
    const snap = await getDocs(q);
    const loadedQuizzes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setQuizzes(loadedQuizzes);
    setLoading(false);

    // After quizzes load, fetch this student's attempt results using deterministic doc IDs.
    // This is a direct getDoc per quiz — no query, no index, no Firestore-rules surprise.
    // Works perfectly on every fresh login and every class-tab switch.
    if (!isAdmin && user?.email) {
      try {
        const checks = loadedQuizzes.map(quiz =>
          getDoc(doc(db, "quizResults", attemptDocId(quiz.id, user.email)))
        );
        const resultDocs = await Promise.all(checks);
        const newIds = new Set();
        const newResults = {};
        for (const d of resultDocs) {
          if (d.exists()) {
            let data = d.data();
            if (data.status === "in_progress") {
              const qz = loadedQuizzes.find(q => q.id === data.quizId);
              if (qz) { try { data = await finaliseAbandoned(qz, data); } catch (e) { console.warn("finalise failed", e); } }
            }
            newIds.add(data.quizId);
            newResults[data.quizId] = data;
          }
        }
        // Merge with any results already loaded by fetchAttempts (other classes, old docs)
        setAttemptedQuizIds(prev => new Set([...prev, ...newIds]));
        setAttemptedResults(prev => ({ ...prev, ...newResults }));
      } catch (e) {
        console.error("Per-quiz attempt check failed:", e);
      }
    }
  };

  // Called by QuizRunner right after successful submission
  // resultData includes score, percent, studentAnswers, etc.
  const handleAttempted = (quizId, resultData) => {
    setAttemptedQuizIds(prev => new Set([...prev, quizId]));
    setAttemptedResults(prev => ({ ...prev, [quizId]: resultData }));
  };

  const isAssigned = (quiz) => {
    if (isAdmin) return true;
    if (!quiz.assignedBatch || quiz.assignedBatch === "Both") return true;
    if (!studentBatch) return false;
    return quiz.assignedBatch === studentBatch;
  };

  const getAvailability = (quiz) => {
    if (isAdmin) return { status: "open", label: null };
    const now = Date.now();
    if (quiz.openFrom && new Date(quiz.openFrom).getTime() > now)
      return { status: "upcoming", label: `Opens: ${new Date(quiz.openFrom).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}` };
    if (quiz.openUntil && new Date(quiz.openUntil).getTime() < now)
      return { status: "expired", label: "Exam window closed" };
    if (quiz.openUntil)
      return { status: "open", label: `Closes: ${new Date(quiz.openUntil).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}` };
    return { status: "open", label: null };
  };

  // ── Review screen ──
  if (reviewQuiz) {
    const result = attemptedResults[reviewQuiz.id] || {};
    return (
      <QuizReview
        quiz={reviewQuiz}
        result={result}
        onExit={() => setReviewQuiz(null)}
      />
    );
  }

  // ── Active quiz runner ──
  if (activeQuiz) {
    const hasAttempted = !isAdmin && attemptedQuizIds.has(activeQuiz.id);
    return (
      <QuizRunner
        quiz={activeQuiz}
        user={user}
        isAdmin={isAdmin}
        assigned={isAssigned(activeQuiz)}
        studentBatch={studentBatch}
        hasAttempted={hasAttempted}
        onAttempted={handleAttempted}
        onExit={() => setActiveQuiz(null)}
      />
    );
  }

  // ── Quiz list ──
  return (
    <div className="class-page">
      <h1 className="class-title">📝 Physics Quizzes</h1>
      <p style={{ color: "#555", marginBottom: "20px", fontSize: "0.95rem" }}>
        Test your knowledge — timed quizzes with Physics equations
        {studentBatch && studentBatch !== "ADMIN" && <span className="batch-badge"> 🏷 Batch {studentBatch}</span>}
        {isAdmin && <span className="batch-badge" style={{ background: "#e65100" }}> 👑 Admin</span>}
      </p>
      <div className="sim-tabs">
        {visibleClasses.map(cls => (
          <button key={cls} className={activeClass === cls ? "active" : ""} onClick={() => setActiveClass(cls)}>{cls}</button>
        ))}
      </div>
      {loading || batchLoading ? <div className="loading">Loading quizzes...</div> :
        (!isStaff && visibleClasses.length === 0) ? (
          <div className="quiz-empty"><div style={{ fontSize: "3rem" }}>🔒</div><p>No quizzes available for you yet.</p></div>
        ) : shownQuizzes.length === 0 ? (
          <div className="quiz-empty"><div style={{ fontSize: "3rem" }}>📭</div><p>No quizzes for {activeClass} yet.</p></div>
        ) : (
          <div className="quiz-list">
            {shownQuizzes.map(quiz => {
              const assigned = isAssigned(quiz);
              const avail = getAvailability(quiz);
              const hasAttempted = !isAdmin && attemptedQuizIds.has(quiz.id);
              const result = attemptedResults[quiz.id];
              const canStart = assigned && !hasAttempted && avail.status === "open";

              const quizIcon = !assigned ? "🔒"
                : hasAttempted ? "✅"
                : avail.status === "upcoming" ? "⏳"
                : avail.status === "expired" ? "🔒"
                : "📝";

              return (
                <div key={quiz.id} className={`quiz-card ${!assigned ? "quiz-card-locked" : ""}`}>
                  <div className="quiz-card-left">
                    <div className="quiz-icon">{quizIcon}</div>
                    <div>
                      <h3>{quiz.title}</h3>
                      <div className="quiz-meta">
                        <span>⏱ {quiz.timeLimit} min</span>
                        <span>❓ {quiz.questions?.length || 0} questions</span>
                        <span className="quiz-type-badge">MCQ + T/F</span>
                        {isStaff && quiz.facultyClassId && (
                          <span style={{ background: "#ede7f6", color: "#4527a0", borderRadius: 12, padding: "2px 10px", fontWeight: 700 }}>📚 Only for class: {classMap[quiz.facultyClassId]?.name || "(deleted class)"}</span>
                        )}
                        {quiz.assignedBatch && quiz.assignedBatch !== "Both" && (
                          <span className={`quiz-batch-badge ${quiz.assignedBatch === "C1" ? "c1" : "c2"}`}>🏷 Batch {quiz.assignedBatch}</span>
                        )}
                      </div>
                      {(() => {
                        const now = Date.now();
                        const f = quiz.openFrom && new Date(quiz.openFrom).getTime();
                        const u = quiz.openUntil && new Date(quiz.openUntil).getTime();
                        const st = f && f > now ? "upcoming" : u && u < now ? "closed" : "open";
                        const chip = st === "upcoming" ? { bg: "#fff8e1", c: "#e65100", t: `⏳ Starts in ${countdown(f - now)}` }
                          : st === "closed" ? { bg: "#fce4ec", c: "#c62828", t: "🔒 Closed" }
                          : { bg: "#e8f5e9", c: "#2e7d32", t: u ? `🟢 Open now · closes in ${countdown(u - now)}` : "🟢 Open now" };
                        return (
                          <div className="quiz-schedule">
                            <span className="quiz-schedule-when">📅 {scheduleText(quiz)}</span>
                            <span className="quiz-schedule-status" style={{ background: chip.bg, color: chip.c }}>{chip.t}</span>
                          </div>
                        );
                      })()}
                      <div style={{ display: "none" }}>
                      </div>
                      {!assigned && (
                        <p className="quiz-locked-msg">🔒 Assigned to <strong>Batch {quiz.assignedBatch}</strong> only. Your batch: <strong>{studentBatch || "Not assigned"}</strong></p>
                      )}
                      {/* Score summary for attempted quizzes */}
                      {hasAttempted && result && (
                        <p style={{ color: "#1565c0", fontSize: "0.82rem", marginTop: 4, fontWeight: 600 }}>
                          ✅ Submitted · Score: {result.score}/{result.total} ({result.percent}%)
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Button area — start, review, or locked */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                    {hasAttempted ? (
                      <>
                        <button
                          className="quiz-start-btn"
                          style={{ background: "#1565c0", fontSize: "0.85rem", padding: "9px 18px" }}
                          onClick={() => setReviewQuiz(quiz)}
                        >
                          📋 Review Answers
                        </button>
                        <span style={{ fontSize: "0.75rem", color: "#2e7d32", fontWeight: 600, textAlign: "right" }}>
                          ✅ Attempted
                        </span>
                      </>
                    ) : (
                      <button
                        className={canStart ? "quiz-start-btn" : "quiz-start-btn quiz-start-btn-locked"}
                        style={
                          avail.status !== "open" && assigned
                            ? { background: "#607d8b", cursor: "not-allowed", opacity: 0.85 }
                            : {}
                        }
                        disabled={assigned && avail.status !== "open"}
                        onClick={async () => {
                          if (!assigned) { setActiveQuiz(quiz); return; }
                          if (!canStart) return;
                          // Double-check Firestore directly before opening quiz —
                          // guards against cases where the page-load query failed silently.
                          try {
                            const snap = await getDoc(doc(db, "quizResults", attemptDocId(quiz.id, user.email)));
                            if (snap.exists()) {
                              // Result already in Firestore — update local state and block
                              const data = snap.data();
                              setAttemptedQuizIds(prev => new Set([...prev, quiz.id]));
                              setAttemptedResults(prev => ({ ...prev, [quiz.id]: data }));
                              alert(data.status === "in_progress"
                                ? "This quiz was already opened once. Only one attempt is allowed — it has been submitted with the answers you gave."
                                : "You have already submitted this quiz. Only one attempt is allowed.");
                              if (data.status === "in_progress") { try { const done = await finaliseAbandoned(quiz, data); setAttemptedResults(prev => ({ ...prev, [quiz.id]: done })); } catch {} }
                              return;
                            }
                          } catch (e) {
                            // If we can't read Firestore, proceed — QuizRunner has its own guard
                            console.warn("Pre-flight attempt check failed:", e);
                          }
                          setActiveQuiz(quiz);
                        }}
                      >
                        {!assigned ? "🔒 View + Answers"
                          : avail.status === "upcoming" ? "⏳ Not Yet Open"
                          : avail.status === "expired" ? "🔒 Exam Closed"
                          : "Start Quiz →"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// QuizReview — shown when a student clicks "Review Answers" on an attempted quiz
// ─────────────────────────────────────────────────────────────────────────────
function QuizReview({ quiz, result, onExit }) {
  const questions = quiz.questions || [];
  const totalQ = questions.length;
  const score = result.score ?? 0;
  const percent = result.percent ?? 0;
  const studentAnswers = result.studentAnswers || {}; // may not exist for old submissions

  const grade =
    percent >= 90 ? { label: "Excellent! 🎉", color: "#2e7d32" } :
    percent >= 75 ? { label: "Great Job! 👍", color: "#1565c0" } :
    percent >= 50 ? { label: "Good Effort! 💪", color: "#f57c00" } :
                    { label: "Keep Practising! 📚", color: "#c62828" };

  const submittedOn = result.submittedAt
    ? new Date(result.submittedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <div className="class-page">
      <div className="result-screen">
        {/* Header */}
        <div className="result-icon">📋</div>
        <h2>{quiz.title}</h2>
        {submittedOn && (
          <p style={{ color: "#888", fontSize: "0.82rem", marginTop: -8, marginBottom: 16 }}>
            Submitted on {submittedOn}
          </p>
        )}

        {/* Score card */}
        <div style={{ display: "flex", justifyContent: "center", gap: 32, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ textAlign: "center" }}>
            <div className="result-score" style={{ color: grade.color }}>{score} / {totalQ}</div>
            <div style={{ fontSize: "0.8rem", color: "#888", marginTop: 2 }}>Score</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div className="result-percent" style={{ color: grade.color }}>{percent}%</div>
            <div style={{ fontSize: "0.8rem", color: "#888", marginTop: 2 }}>Percentage</div>
          </div>
        </div>
        <div className="result-grade" style={{ color: grade.color, marginBottom: 8 }}>{grade.label}</div>

        {/* Answer review */}
        <div className="result-review">
          <h3>📖 Question-wise Review</h3>
          {!result.studentAnswers && (
            <p style={{ color: "#f57c00", fontSize: "0.82rem", marginBottom: 12, background: "#fff8e1", padding: "8px 12px", borderRadius: 8 }}>
              ℹ️ Detailed answer breakdown is available for quizzes taken after the latest update. Correct answers are highlighted below.
            </p>
          )}

          {questions.map((q, i) => {
            const userAns = studentAnswers[i];
            const hasUserAns = userAns !== undefined;
            const correct = hasUserAns
              ? (q.type === "truefalse"
                  ? String(userAns) === String(q.answer)
                  : Number(userAns) === Number(q.answer))
              : null; // null = unknown (old submission without answers saved)

            const correctLabel = q.type === "truefalse"
              ? (q.answer === true || q.answer === "true" ? "True" : "False")
              : <LatexRenderer text={q.options?.[q.answer] || ""} />;

            const userLabel = !hasUserAns ? null
              : q.type === "truefalse"
                ? (String(userAns) === "true" ? "True" : "False")
                : <LatexRenderer text={q.options?.[userAns] || ""} />;

            const itemStyle = correct === null
              ? { background: "#f5f5f5", borderColor: "#ddd" }
              : correct
                ? { background: "#e8f5e9", borderColor: "#a5d6a7" }
                : { background: "#fce4ec", borderColor: "#f48fb1" };

            return (
              <div key={i} className="review-item" style={itemStyle}>
                <p className="review-q">
                  <strong>Q{i + 1}.</strong> <LatexRenderer text={q.question} />
                </p>
                {q.imageUrl && <img src={q.imageUrl} alt="question" className="question-img-small" style={{ maxWidth: "100%", borderRadius: 8, margin: "6px 0" }} />}

                {/* MCQ options with highlights */}
                {q.type === "mcq" && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                    {q.options?.map((opt, oi) => {
                      const isCorrect = oi === Number(q.answer);
                      const isUserChoice = hasUserAns && oi === Number(userAns);
                      let optStyle = { padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: "0.88rem", display: "flex", alignItems: "center", gap: 10 };
                      if (isCorrect) optStyle = { ...optStyle, background: "#e8f5e9", border: "1.5px solid #2e7d32", fontWeight: 600 };
                      if (isUserChoice && !isCorrect) optStyle = { ...optStyle, background: "#fce4ec", border: "1.5px solid #e53935" };
                      return (
                        <div key={oi} style={optStyle}>
                          <span style={{ background: isCorrect ? "#2e7d32" : isUserChoice ? "#e53935" : "#9e9e9e", color: "white", width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 700, flexShrink: 0 }}>
                            {["A","B","C","D"][oi]}
                          </span>
                          <LatexRenderer text={opt} />
                          {isCorrect && <span style={{ marginLeft: "auto", color: "#2e7d32", fontWeight: 700 }}>✅ Correct</span>}
                          {isUserChoice && !isCorrect && <span style={{ marginLeft: "auto", color: "#e53935", fontWeight: 700 }}>❌ Your answer</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* True/False display */}
                {q.type === "truefalse" && (
                  <div style={{ marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {["true", "false"].map(val => {
                      const isCorrect = String(q.answer) === val;
                      const isUserChoice = hasUserAns && String(userAns) === val;
                      let s = { padding: "6px 18px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: "0.88rem", fontWeight: 500 };
                      if (isCorrect) s = { ...s, background: "#e8f5e9", border: "1.5px solid #2e7d32", color: "#2e7d32", fontWeight: 700 };
                      if (isUserChoice && !isCorrect) s = { ...s, background: "#fce4ec", border: "1.5px solid #e53935", color: "#e53935", fontWeight: 700 };
                      return (
                        <div key={val} style={s}>
                          {val === "true" ? "True" : "False"}
                          {isCorrect && " ✅"}
                          {isUserChoice && !isCorrect && " ❌"}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Summary line */}
                {hasUserAns && (
                  <p style={{ marginTop: 8, fontSize: "0.82rem", color: correct ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                    {correct ? "✅ Correct" : `❌ Your answer: ${userLabel} · Correct: ${typeof correctLabel === "string" ? correctLabel : ""}`}
                  </p>
                )}
                {!hasUserAns && (
                  <p style={{ marginTop: 8, fontSize: "0.82rem", color: "#1565c0", fontWeight: 600 }}>
                    ✔ Correct answer: {correctLabel}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <button className="quiz-exit-btn" style={{ marginTop: 24 }} onClick={onExit}>
          ← Back to Quizzes
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// QuizRunner
// ─────────────────────────────────────────────────────────────────────────────
function QuizRunner({ quiz, user, isAdmin, assigned, studentBatch, hasAttempted, onAttempted, onExit }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(quiz.timeLimit * 60);
  const [saving, setSaving] = useState(false);
  const locked = assigned && !isAdmin;           // lock only for real student attempts
  const [started, setStarted] = useState(!locked);
  const [starting, setStarting] = useState(false);
  const [violations, setViolations] = useState(0);
  const [warning, setWarning] = useState(false);
  const violationsRef = useRef(0);
  const lastViolation = useRef(0);
  const timerRef = useRef(null);
  const answersRef = useRef({});
  const submittedRef = useRef(false); // prevents timer + button both firing
  const questions = quiz.questions || [];
  const current = questions[currentIdx];
  const totalQ = questions.length;

  useEffect(() => { answersRef.current = answers; }, [answers]);

  useEffect(() => {
    if (!assigned || hasAttempted || !started) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); doSubmit(answersRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [assigned, hasAttempted, started]);

  const docRef = () => doc(db, "quizResults", attemptDocId(quiz.id, user.email || "unknown"));

  // ── Start: record the attempt immediately, then go fullscreen ──
  const startAttempt = async () => {
    setStarting(true);
    try {
      const snap = await getDoc(docRef());
      if (snap.exists()) { alert("This quiz has already been attempted. Only one attempt is allowed."); onExit(); return; }
      await setDoc(docRef(), {
        quizId: quiz.id, quizTitle: quiz.title, class: quiz.class,
        assignedBatch: quiz.assignedBatch || "Both",
        studentBatch: studentBatch && studentBatch !== "ADMIN" ? studentBatch : "—",
        studentName: user.displayName || "Unknown", studentEmail: user.email || "Unknown",
        status: "in_progress", startedAt: new Date().toISOString(),
        studentAnswers: {}, violations: 0, score: 0, total: totalQ, percent: 0, timestamp: Date.now(),
      });
    } catch (e) { alert("Could not start the quiz: " + e.message); setStarting(false); return; }
    try { await document.documentElement.requestFullscreen?.(); } catch {}
    setStarting(false); setStarted(true);
  };

  // ── Lock: detect leaving the screen, block copy / right-click / shortcuts ──
  useEffect(() => {
    if (!locked || !started || submitted) return;
    document.body.classList.add("quiz-locked");
    const flag = (why) => {
      if (submittedRef.current) return;
      const now = Date.now(); if (now - lastViolation.current < 1500) return; // one event per exit
      lastViolation.current = now;
      violationsRef.current += 1; setViolations(violationsRef.current); setWarning(true);
      setDoc(docRef(), { violations: violationsRef.current, lastViolation: why }, { merge: true }).catch(() => {});
      if (violationsRef.current >= MAX_VIOLATIONS) doSubmit(answersRef.current, `Auto-submitted: left the quiz screen ${MAX_VIOLATIONS} times`);
    };
    const onVis = () => { if (document.hidden) flag("switched tab / app"); };
    const onBlur = () => flag("left the quiz window");
    const onFs = () => { if (!document.fullscreenElement) flag("exited full screen"); };
    const block = e => e.preventDefault();
    const onKey = e => {
      const k = (e.key || "").toLowerCase();
      if (k === "f12" || k === "printscreen" || ((e.ctrlKey || e.metaKey) && ["c", "v", "x", "p", "s", "u", "a"].includes(k))) e.preventDefault();
    };
    const onUnload = e => { e.preventDefault(); e.returnValue = ""; };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFs);
    ["copy", "cut", "paste", "contextmenu", "selectstart"].forEach(t => document.addEventListener(t, block));
    document.addEventListener("keydown", onKey);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.body.classList.remove("quiz-locked");
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFs);
      ["copy", "cut", "paste", "contextmenu", "selectstart"].forEach(t => document.removeEventListener(t, block));
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [locked, started, submitted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save answers as they are chosen, so a closed tab still counts what was done
  useEffect(() => {
    if (!locked || !started || submittedRef.current) return;
    const t = setTimeout(() => { setDoc(docRef(), { studentAnswers: answers }, { merge: true }).catch(() => {}); }, 600);
    return () => clearTimeout(t);
  }, [answers, locked, started]); // eslint-disable-line react-hooks/exhaustive-deps

  const returnToQuiz = async () => { setWarning(false); try { await document.documentElement.requestFullscreen?.(); } catch {} };

  const formatTime = s => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
  const timerColor = timeLeft < 30 ? "#c62828" : timeLeft < 60 ? "#f57c00" : "#1a237e";


  const doSubmit = async (ans, reason) => {
    if (!assigned || submittedRef.current) return;
    submittedRef.current = true;
    clearInterval(timerRef.current);
    const score = scoreAnswers(questions, ans);
    const percent = Math.round((score / totalQ) * 100);
    setSaving(true);
    const resultData = {
      quizId: quiz.id, quizTitle: quiz.title, class: quiz.class,
      assignedBatch: quiz.assignedBatch || "Both",
      studentBatch: studentBatch && studentBatch !== "ADMIN" ? studentBatch : "—",
      studentName: user.displayName || "Unknown", studentEmail: user.email || "Unknown",
      score, total: totalQ, percent,
      studentAnswers: ans,           // ← saved so review screen shows their specific answers
      submittedAt: new Date().toISOString(), timestamp: Date.now(),
      status: "submitted", violations: violationsRef.current,
      ...(reason ? { autoSubmitted: reason } : {}),
    };
    try {
      // Use a deterministic document ID: setDoc prevents duplicate results even if
      // the student somehow submits twice (e.g. timer + button, or two browser tabs).
      const docId = attemptDocId(quiz.id, user.email || "unknown");
      await setDoc(doc(db, "quizResults", docId), resultData);
      // Update parent immediately so list shows "Review Answers" when they go back
      onAttempted(quiz.id, resultData);
    } catch (e) {
      console.error("Save failed:", e);
      submittedRef.current = false;
    }
    setSaving(false);
    setSubmitted(true);
    setWarning(false);
    try { if (document.fullscreenElement) document.exitFullscreen(); } catch {}
  };

  // ── Already attempted guard ──
  if (hasAttempted && !submitted) {
    return (
      <div className="class-page">
        <div style={{ textAlign: "center", padding: "50px 24px", background: "white", borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", maxWidth: 500, margin: "40px auto" }}>
          <div style={{ fontSize: "3.5rem", marginBottom: 12 }}>✅</div>
          <h2 style={{ color: "#2e7d32", marginBottom: 8 }}>Already Attempted</h2>
          <p style={{ color: "#555", marginBottom: 24 }}>
            You have already submitted <strong>{quiz.title}</strong>.<br />
            Only <strong>one attempt per student</strong> is allowed.
          </p>
          <button className="quiz-exit-btn" onClick={onExit}>← Back to Quizzes</button>
        </div>
      </div>
    );
  }

  // ── Locked batch view ──
  if (!assigned) {
    return (
      <div className="class-page">
        <div className="locked-screen">
          <div style={{ fontSize: "3rem", marginBottom: 8 }}>🔒</div>
          <h2>{quiz.title}</h2>
          <p>This quiz is assigned to <strong>Batch {quiz.assignedBatch}</strong> only.</p>
          <p style={{ color: "#888", fontSize: "0.9rem", marginTop: 4 }}>Your batch: <strong>{studentBatch || "Not assigned"}</strong></p>
          <div style={{ background: "#fff8e1", border: "1px solid #f9a825", borderRadius: 8, padding: "10px 16px", margin: "16px 0", fontSize: "0.88rem", color: "#e65100" }}>
            📖 You can view questions and answers below for study purposes.
          </div>
          <div className="locked-questions">
            {questions.map((q, i) => {
              const correctIdx = q.type === "mcq" ? Number(q.answer) : null;
              const correctTF = q.type === "truefalse" ? (String(q.answer) === "true" ? "True" : "False") : null;
              return (
                <div key={i} className="quiz-question-card" style={{ textAlign: "left", marginTop: 16 }}>
                  <div className="quiz-q-type-badge">{q.type === "truefalse" ? "True / False" : "Multiple Choice"}</div>
                  <h3 className="quiz-question-text">Q{i + 1}.&nbsp;<LatexRenderer text={q.question} /></h3>
                  {q.imageUrl && <img src={q.imageUrl} alt="question" className="question-img" style={{ maxWidth: "100%", borderRadius: 8, margin: "10px 0" }} />}
                  {q.type === "mcq" && (
                    <div className="quiz-options" style={{ pointerEvents: "none" }}>
                      {q.options?.map((opt, oi) => (
                        <div key={oi} className="quiz-option"
                          style={oi === correctIdx ? { background: "#e8f5e9", border: "2px solid #2e7d32" } : {}}>
                          <span className="option-letter">{["A","B","C","D"][oi]}</span>
                          <LatexRenderer text={opt} />
                          {oi === correctIdx && <span style={{ marginLeft: "auto", color: "#2e7d32", fontWeight: 700 }}>✅</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {q.type === "truefalse" && (
                    <div style={{ marginTop: 10, padding: "8px 14px", background: "#e8f5e9", border: "1px solid #2e7d32", borderRadius: 8, color: "#2e7d32", fontWeight: 700 }}>
                      ✅ Answer: {correctTF}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button className="quiz-exit-btn" style={{ marginTop: 24 }} onClick={onExit}>← Back to Quizzes</button>
        </div>
      </div>
    );
  }

  // ── Result screen ──
  if (submitted) {
    const score = scoreAnswers(questions, answers);
    const percent = Math.round((score / totalQ) * 100);
    const grade =
      percent >= 90 ? { label: "Excellent! 🎉", color: "#2e7d32" } :
      percent >= 75 ? { label: "Great Job! 👍", color: "#1565c0" } :
      percent >= 50 ? { label: "Good Effort! 💪", color: "#f57c00" } :
                      { label: "Keep Practising! 📚", color: "#c62828" };
    return (
      <div className="class-page">
        <div className="result-screen">
          <div className="result-icon">🏆</div>
          <h2>{quiz.title}</h2>
          <div className="result-score" style={{ color: grade.color }}>{score} / {totalQ}</div>
          <div className="result-percent" style={{ color: grade.color }}>{percent}%</div>
          <div className="result-grade" style={{ color: grade.color }}>{grade.label}</div>
          {saving && <p style={{ color: "#f57c00", fontSize: "0.88rem" }}>⏳ Saving your result...</p>}

          <div className="result-review">
            <h3>📋 Answer Review</h3>
            {questions.map((q, i) => {
              const userAns = answers[i];
              const correct = q.type === "truefalse"
                ? String(userAns) === String(q.answer)
                : Number(userAns) === Number(q.answer);
              const userLabel = userAns === undefined
                ? <span style={{ color: "#999" }}>Not answered</span>
                : q.type === "truefalse"
                  ? (String(userAns) === "true" ? "True" : "False")
                  : <LatexRenderer text={q.options?.[userAns] || ""} />;
              const correctLabel = q.type === "truefalse"
                ? (q.answer === true || q.answer === "true" ? "True" : "False")
                : <LatexRenderer text={q.options?.[q.answer] || ""} />;
              return (
                <div key={i} className={`review-item ${correct ? "correct" : "wrong"}`}>
                  <p className="review-q"><strong>Q{i + 1}.</strong> <LatexRenderer text={q.question} /></p>
                  {q.imageUrl && <img src={q.imageUrl} alt="question" className="question-img-small" />}
                  <p className="review-ans">{correct ? "✅" : "❌"} Your answer: <strong>{userLabel}</strong></p>
                  {!correct && <p className="review-correct">✔ Correct answer: <strong>{correctLabel}</strong></p>}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 20, padding: "12px 18px", background: "#f3e5f5", borderRadius: 10, fontSize: "0.85rem", color: "#4a148c" }}>
            🔒 This was your <strong>one and only attempt</strong>. Your score has been recorded.
            You can review these answers anytime from the quiz list.
          </div>
          <button className="quiz-exit-btn" style={{ marginTop: 20 }} onClick={onExit}>
            ← Back to Quizzes
          </button>
        </div>
      </div>
    );
  }

  // ── Start gate (students only) ──
  if (locked && !started && !submitted) {
    return (
      <div className="class-page">
        <div className="quiz-gate">
          <h2>📝 {quiz.title}</h2>
          <p className="quiz-gate-meta">⏱ {quiz.timeLimit} minutes · ❓ {totalQ} questions</p>
          <ul>
            <li>🔒 <strong>One attempt only.</strong> Once you press Start, this quiz counts as attempted, even if you close the page.</li>
            <li>🖥️ The quiz opens in <strong>full screen</strong>. Stay on this screen until you submit.</li>
            <li>⚠️ Switching tabs or apps, minimising, or leaving full screen is recorded. After <strong>{MAX_VIOLATIONS} times</strong> your quiz is submitted automatically.</li>
            <li>🚫 Copy, paste and right-click are disabled.</li>
            <li>💾 Your answers are saved as you go.</li>
          </ul>
          <div className="quiz-gate-btns">
            <button className="quiz-nav-btn" onClick={onExit}>← Not now</button>
            <button className="quiz-submit-btn" disabled={starting} onClick={startAttempt}>{starting ? "Starting…" : "I understand, start the quiz →"}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Active quiz ──
  return (
    <div className="class-page quiz-lock-area">
      {warning && !submitted && (
        <div className="quiz-warn-overlay">
          <div className="quiz-warn-card">
            <div style={{ fontSize: "3rem" }}>⚠️</div>
            <h2>You left the quiz screen</h2>
            <p>Warning <strong>{violations} of {MAX_VIOLATIONS}</strong>. At {MAX_VIOLATIONS}, your quiz is submitted automatically.</p>
            <button className="quiz-submit-btn" onClick={returnToQuiz}>Return to the quiz</button>
          </div>
        </div>
      )}
      {locked && violations > 0 && !warning && (
        <p className="quiz-violation-note">⚠️ Left the quiz screen {violations} of {MAX_VIOLATIONS} times</p>
      )}
      <div className="quiz-runner-header">
        <div className="quiz-runner-title">{quiz.title}</div>
        <div className="quiz-timer" style={{ color: timerColor, borderColor: timerColor }}>⏱ {formatTime(timeLeft)}</div>
      </div>
      <div className="quiz-progress-bar">
        <div className="quiz-progress-fill" style={{ width: `${((currentIdx + 1) / totalQ) * 100}%` }} />
      </div>
      <p className="quiz-progress-text">Question {currentIdx + 1} of {totalQ} &nbsp;|&nbsp; {Object.keys(answers).length} answered</p>

      <div className="quiz-question-card">
        <div className="quiz-q-type-badge">{current.type === "truefalse" ? "True / False" : "Multiple Choice"}</div>
        <h2 className="quiz-question-text">Q{currentIdx + 1}.&nbsp;<LatexRenderer text={current.question} /></h2>
        {current.imageUrl && (
          <div className="question-img-container">
            <img src={current.imageUrl} alt="Question diagram" className="question-img" />
          </div>
        )}
        {current.type === "truefalse" ? (
          <div className="quiz-options">
            {["true", "false"].map(val => (
              <button key={val}
                className={`quiz-option ${String(answers[currentIdx]) === val ? "selected" : ""}`}
                onClick={() => setAnswers(prev => ({ ...prev, [currentIdx]: val }))}>
                {val === "true" ? "✅ True" : "❌ False"}
              </button>
            ))}
          </div>
        ) : (
          <div className="quiz-options">
            {current.options?.map((opt, oi) => (
              <button key={oi}
                className={`quiz-option ${Number(answers[currentIdx]) === oi ? "selected" : ""}`}
                onClick={() => setAnswers(prev => ({ ...prev, [currentIdx]: oi }))}>
                <span className="option-letter">{["A","B","C","D"][oi]}</span>
                <LatexRenderer text={opt} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="quiz-nav">
        <button className="quiz-nav-btn" disabled={currentIdx === 0} onClick={() => setCurrentIdx(i => i - 1)}>← Previous</button>
        <div className="quiz-dot-nav">
          {questions.map((_, i) => (
            <button key={i}
              className={`quiz-dot ${i === currentIdx ? "active" : ""} ${answers[i] !== undefined ? "answered" : ""}`}
              onClick={() => setCurrentIdx(i)}>{i + 1}
            </button>
          ))}
        </div>
        {currentIdx < totalQ - 1
          ? <button className="quiz-nav-btn" onClick={() => setCurrentIdx(i => i + 1)}>Next →</button>
          : <button className="quiz-submit-btn" onClick={() => doSubmit(answers)}>Submit Quiz ✓</button>
        }
      </div>
    </div>
  );
}
